// 2.1 — the inline start card. Rendered under the assistant message that offers
// the form. Any *registry* form can be offered (multiform slice) — an offer for
// a form the widget holds no definition for still renders nothing, exactly as
// before. A card for an already-started form never renders; that suppression
// lives at both ends (the turn does not attach `formOfferId` for a started,
// submitted, discarded, or queued form, and this component re-checks so a
// persisted older message cannot resurrect the offer).

import { useRef } from 'react';
import styles from './ConversationalForms.module.css';
import { useConversationalForms } from './FormsContext';
import { ClipboardIcon } from './icons';

const StartCard = ({ formId }: { formId: string }) => {
    const { active, registry, state, startForm } = useConversationalForms();
    const cardRef = useRef<HTMLDivElement>(null);

    const offered = registry?.byId.get(formId);
    if (!active || !offered) return null;
    // An ambient (non-browsable) form is never visitor-startable: even a
    // server offer naming it renders no card (store tracker 6.30). Re-checked
    // here so a persisted older message cannot resurrect one either.
    if (!offered.browsable) return null;
    if (
        state.activeFormId !== null ||
        state.startedFormIds.includes(formId) ||
        state.submittedFormIds.includes(formId) ||
        state.discardedFormIds.includes(formId) ||
        state.queuedForms.includes(formId)
    ) {
        return null;
    }

    const onStart = () => {
        // Capture the card's screen rect so the pinned card can visibly float up
        // from this spot (FLIP) — the continuity is the point of the gesture.
        startForm(formId, cardRef.current?.getBoundingClientRect() ?? null);
    };

    return (
        <div className={styles.startCard} ref={cardRef} data-testid="cf-start-card">
            <span className={styles.startIcon}>
                <ClipboardIcon />
            </span>
            <span className={styles.startText}>
                <span className={styles.startTitle}>{offered.cardTitle}</span>
                <span className={styles.startSub}>{offered.cardSub}</span>
            </span>
            <button type="button" className={styles.startButton} onClick={onStart}>
                Start
            </button>
        </div>
    );
};

export default StartCard;
